import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Image, FileText } from "lucide-react";
import BootSequence from "@/components/os/BootSequence";
import TopPanel from "@/components/os/TopPanel";
import DesktopIcon from "@/components/os/DesktopIcon";
import WindowFrame from "@/components/os/WindowFrame";
import ConkyWidget from "@/components/os/ConkyWidget";
import MobileShell from "@/components/os/MobileShell";
import { DESKTOP_ICONS, appMeta, renderAppContent } from "@/components/os/apps";
import { IDENTITY, hydrateContent } from "@/data/content";
import { playBeep } from "@/lib/sound";

const useIsMobile = () => {
    const [mobile, setMobile] = useState(() => window.innerWidth < 768);
    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)");
        const onChange = (e) => setMobile(e.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);
    return mobile;
};

export default function Desktop() {
    const [booted, setBooted] = useState(false);
    const isMobile = useIsMobile();
    const [wins, setWins] = useState([]);
    const zRef = useRef(10);
    const [activeKey, setActiveKey] = useState(null);

    useEffect(() => {
        hydrateContent();
    }, []);

    const openWin = useCallback((key) => {
        playBeep(760, 0.05, 0.045, "square");
        setWins((list) => {
            const existing = list.find((w) => w.key === key);
            if (existing) {
                return list.map((w) => (w.key === key ? { ...w, minimized: false, z: ++zRef.current } : w));
            }
            const n = list.length;
            return [
                ...list,
                {
                    key,
                    title: appMeta(key).title,
                    z: ++zRef.current,
                    minimized: false,
                    pos: { x: 40 + (n % 6) * 34, y: 24 + (n % 6) * 26 },
                },
            ];
        });
        setActiveKey(key);
    }, []);

    const closeWin = useCallback((key) => {
        playBeep(420, 0.05, 0.045, "square");
        setWins((list) => list.filter((w) => w.key !== key));
        setActiveKey((cur) => (cur === key ? null : cur));
    }, []);

    const minimizeWin = useCallback((key) => {
        playBeep(500, 0.04, 0.04, "square");
        setWins((list) => list.map((w) => (w.key === key ? { ...w, minimized: true } : w)));
    }, []);

    const focusWin = useCallback((key) => {
        setActiveKey(key);
        setWins((list) => list.map((w) => (w.key === key ? { ...w, z: ++zRef.current } : w)));
    }, []);

    const onTaskClick = useCallback(
        (key) => {
            const w = wins.find((x) => x.key === key);
            if (w?.minimized) {
                setWins((list) => list.map((x) => (x.key === key ? { ...x, minimized: false, z: ++zRef.current } : x)));
            } else {
                focusWin(key);
            }
            setActiveKey(key);
        },
        [wins, focusWin]
    );

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && activeKey && !isMobile) closeWin(activeKey);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [activeKey, closeWin, isMobile]);

    const finishBoot = () => {
        setBooted(true);
        if (!isMobile) setTimeout(() => openWin("about"), 500);
    };

    return (
        <div className="h-[100dvh] w-screen overflow-hidden wallpaper relative">
            <div className="os-noise" aria-hidden="true" />
            {!booted && <BootSequence onDone={finishBoot} />}

            {isMobile ? (
                booted && <MobileShell />
            ) : (
                <>
                    <TopPanel
                        windows={wins}
                        activeKey={activeKey}
                        onTaskClick={onTaskClick}
                        onLogoClick={() => setWins([])}
                    />
                    <div className="relative h-[calc(100dvh-2.5rem)]" data-testid="desktop">
                        <div className="absolute left-3 top-4 grid grid-cols-2 sm:grid-cols-1 gap-1 z-10">
                            {DESKTOP_ICONS.map((d) => (
                                <DesktopIcon key={d.key} {...d} onOpen={() => openWin(d.key)} />
                            ))}
                        </div>

                        <a
                            href={IDENTITY.resume}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="desktop-icon-resume"
                            className="absolute left-3 bottom-4 z-10 hidden sm:flex flex-col items-center gap-1.5 w-[84px] py-2 rounded-lg hover:bg-white/10 transition-colors"
                        >
                            <span className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#23262d] border border-white/15 text-neutral-200 shadow-[0_8px_24px_rgba(0,0,0,0.4)]">
                                <FileText size={22} strokeWidth={1.8} />
                            </span>
                            <span className="text-[11px] font-medium text-neutral-200 [text-shadow:0_1px_3px_rgba(0,0,0,0.8)]">Resume.pdf</span>
                        </a>

                        <ConkyWidget />

                        <div
                            aria-hidden="true"
                            className="absolute bottom-4 right-5 text-right font-mono text-[10px] uppercase tracking-[0.25em] text-white/25 select-none"
                        >
                            wikiOS 26.04 LTS
                            <br />
                            {IDENTITY.name}
                        </div>

                        <AnimatePresence>
                            {wins
                                .filter((w) => !w.minimized)
                                .map((w) => {
                                    const meta = appMeta(w.key);
                                    return (
                                        <WindowFrame
                                            key={w.key}
                                            winKey={w.key}
                                            title={w.title}
                                            icon={meta.icon || Image}
                                            focused={activeKey === w.key}
                                            zIndex={w.z}
                                            initial={w.pos}
                                            onClose={() => closeWin(w.key)}
                                            onMinimize={() => minimizeWin(w.key)}
                                            onFocus={() => focusWin(w.key)}
                                            sizeClass={w.key.startsWith("project:") ? "w-[min(94vw,620px)] h-[min(80vh,620px)]" : meta.size}
                                            contentClass={w.key.startsWith("project:") ? "bg-[#f6f5f4] text-[#1a1a1a]" : meta.contentClass}
                                        >
                                            {renderAppContent(w.key, (id) => openWin(`project:${id}`))}
                                        </WindowFrame>
                                    );
                                })}
                        </AnimatePresence>
                    </div>
                </>
            )}
        </div>
    );
}
